/**
 * The public site's URLs: one table for the router, links, the language
 * switcher, hreflang, sitemap, prerender and redirects.
 *
 * Two layouts:
 * - "root" (zettl.cz): Czech at the root with Czech slugs, `/kontakt`.
 * - "legacy" (zettlapp.com before the move): every page under its language,
 *   `/cs/contact`. Still prerendered and routed, so old links and bookmarks
 *   resolve; zettl.cz and zettlapp.com redirect them to the root slugs.
 * English is `/en/…` in both. App routes (`/cs/dashboard` …) are not public
 * pages and never move.
 */

export type SiteLayout = "legacy" | "root";
export type SiteLanguage = "cs" | "en";

/** The pages zettlapp.com had before the move: they keep their legacy `/cs/…` paths and redirects. */
export const LEGACY_PAGE_KEYS = [
  "landing",
  "contact",
  "terms",
  "privacy",
  "dpa",
  "cookies",
  "changelog",
] as const;

/**
 * Guide pages ("průvodce"): one per job Zettl does, born on zettl.cz, so they
 * exist only in the root layout. Their content: `src/lib/guides/`.
 */
export const GUIDE_PAGE_KEYS = [
  "guides",
  "tools",
  "guideAccountants",
  "guidePohoda",
  "guidePohodaHowTo",
  "guideMatching",
  "guideMonthClose",
  "guideFioApi",
  "guideFioToken",
  "guideGpcImport",
  "toolGpcToCsv",
  "toolIsdocViewer",
  "toolQrPayment",
  "toolAccountIban",
  "toolGpcPohoda",
  "guideEmailInbox",
  "guideAccountant",
  "guideIsdoc",
  "guideForeignCurrency",
  "guideSplitPayment",
  "guideMcp",
  "guideVsSpreadsheet",
  "guideFakturoid",
  "guideIdoklad",
] as const;
export type GuidePageKey = (typeof GUIDE_PAGE_KEYS)[number];

export const PUBLIC_PAGE_KEYS = [...LEGACY_PAGE_KEYS, ...GUIDE_PAGE_KEYS] as const;
export type PublicPageKey = (typeof PUBLIC_PAGE_KEYS)[number];

export function isGuidePageKey(key: string): key is GuidePageKey {
  return (GUIDE_PAGE_KEYS as readonly string[]).includes(key);
}

/** The two hub pages in the guide table: they list pages rather than being one. */
export const HUB_PAGE_KEYS = ["guides", "tools"] as const;
export type HubPageKey = (typeof HUB_PAGE_KEYS)[number];

export function isHubPageKey(key: string): key is HubPageKey {
  return (HUB_PAGE_KEYS as readonly string[]).includes(key);
}

/** The segment after `/en` (and after `/cs` in the legacy layout). */
export const PUBLIC_SEGMENTS: Record<PublicPageKey, string> = {
  landing: "",
  contact: "contact",
  terms: "terms",
  privacy: "privacy",
  dpa: "dpa",
  cookies: "cookies",
  changelog: "changelog",
  guides: "guides",
  tools: "free-tools",
  guideAccountants: "for-accountants",
  guidePohoda: "zettl-and-pohoda",
  guidePohodaHowTo: "pohoda-guide",
  guideMatching: "match-payments-with-receipts",
  guideMonthClose: "monthly-close-checklist",
  guideFioApi: "fio-api-bookkeeping",
  guideFioToken: "fio-api-token",
  guideGpcImport: "fio-gpc-abo-statement-import",
  toolGpcToCsv: "gpc-to-csv-converter",
  toolIsdocViewer: "isdoc-viewer",
  toolQrPayment: "qr-payment-generator",
  toolAccountIban: "account-number-to-iban",
  toolGpcPohoda: "gpc-to-pohoda",
  guideEmailInbox: "receipts-by-email",
  guideAccountant: "monthly-documents-for-accountant",
  guideIsdoc: "isdoc-invoice-export",
  guideForeignCurrency: "foreign-currency-invoice-cnb-rate",
  guideSplitPayment: "one-payment-several-invoices",
  guideMcp: "ai-assistant-mcp-bookkeeping",
  guideVsSpreadsheet: "zettl-vs-spreadsheet",
  guideFakturoid: "zettl-and-fakturoid",
  guideIdoklad: "zettl-and-idoklad",
};

/** Czech pages at the root of zettl.cz. */
export const CZECH_ROOT_PATHS: Record<PublicPageKey, string> = {
  landing: "/",
  contact: "/kontakt",
  terms: "/podminky",
  privacy: "/ochrana-osobnich-udaju",
  dpa: "/zpracovatelska-smlouva",
  cookies: "/cookies",
  changelog: "/novinky",
  guides: "/pruvodce",
  tools: "/nastroje-zdarma",
  guideAccountants: "/pro-ucetni",
  guidePohoda: "/zettl-a-pohoda",
  guidePohodaHowTo: "/navod-pohoda",
  guideMatching: "/parovani-plateb-s-doklady",
  guideMonthClose: "/mesicni-uzaverka-osvc",
  guideFioApi: "/fio-api-ucetnictvi",
  guideFioToken: "/fio-api-token",
  guideGpcImport: "/import-vypisu-gpc-abo-fio",
  toolGpcToCsv: "/prevod-gpc-do-csv",
  toolIsdocViewer: "/isdoc-prohlizec",
  toolQrPayment: "/qr-platba-generator",
  toolAccountIban: "/prevod-cisla-uctu-na-iban",
  toolGpcPohoda: "/gpc-do-pohody",
  guideEmailInbox: "/uctenky-a-faktury-e-mailem",
  guideAccountant: "/podklady-pro-ucetni",
  guideIsdoc: "/isdoc-export-faktur",
  guideForeignCurrency: "/faktura-v-cizi-mene-kurz-cnb",
  guideSplitPayment: "/jedna-platba-vice-faktur",
  guideMcp: "/ai-asistent-ucetnictvi-mcp",
  guideVsSpreadsheet: "/zettl-nebo-tabulka",
  guideFakturoid: "/zettl-a-fakturoid",
  guideIdoklad: "/zettl-a-idoklad",
};

/**
 * A guide page has no legacy path: its Czech URL is the root slug in both
 * layouts.
 */
export function publicPath(key: PublicPageKey, lang: SiteLanguage, layout: SiteLayout): string {
  if (lang === "cs" && (layout === "root" || isGuidePageKey(key))) return CZECH_ROOT_PATHS[key];
  const segment = PUBLIC_SEGMENTS[key];
  return segment ? `/${lang}/${segment}` : `/${lang}`;
}

export interface PublicPageMatch {
  key: PublicPageKey;
  lang: SiteLanguage;
  /** `null` for English pages, which are the same in both layouts. */
  layout: SiteLayout | null;
}

function cleanPath(pathname: string): string {
  const path = pathname.split(/[?#]/)[0] || "/";
  return path.length > 1 ? path.replace(/\/+$/, "") || "/" : path;
}

const ROOT_KEY_BY_PATH = new Map<string, PublicPageKey>(
  PUBLIC_PAGE_KEYS.map((key) => [CZECH_ROOT_PATHS[key], key])
);
const KEY_BY_SEGMENT = new Map<string, PublicPageKey>(
  PUBLIC_PAGE_KEYS.map((key) => [PUBLIC_SEGMENTS[key], key])
);

/** Which public page a path is, in which language and layout; `null` for anything else. */
export function matchPublicPath(pathname: string): PublicPageMatch | null {
  const path = cleanPath(pathname);
  const rootKey = ROOT_KEY_BY_PATH.get(path);
  if (rootKey) return { key: rootKey, lang: "cs", layout: "root" };
  const match = path.match(/^\/(cs|en)(?:\/([^/]+))?$/);
  if (!match) return null;
  const key = KEY_BY_SEGMENT.get(match[2] ?? "");
  if (!key) return null;
  const lang = match[1] as SiteLanguage;
  return { key, lang, layout: lang === "en" ? null : "legacy" };
}

/**
 * The language a URL is in: its `/cs` or `/en` prefix, or Czech for the
 * root-layout pages (`/`, `/kontakt` …). `null` when the URL does not say.
 */
export function languageFromPath(pathname: string): SiteLanguage | null {
  const prefixed = cleanPath(pathname).match(/^\/(cs|en)(?:\/|$)/)?.[1];
  if (prefixed) return prefixed as SiteLanguage;
  return matchPublicPath(pathname)?.lang ?? null;
}

/** The same page in another language, in the layout the visitor is on. */
export function alternatePublicPath(
  pathname: string,
  lang: SiteLanguage,
  fallbackLayout: SiteLayout
): string | null {
  const match = matchPublicPath(pathname);
  if (!match) return null;
  return publicPath(match.key, lang, match.layout ?? fallbackLayout);
}
